import { cn } from "@heroui/react";
import { ErrorView } from "src/general/errors/ErrorView";
import type { SlotsToClasses } from "src/utils/componentUtils";
import { PageBreadcrumbs, type PageBreadcrumbsProps } from "./PageBreadcrumbs";

/**
 * Content widths a page can ask for, named after the Tailwind `max-w-*` step
 * each maps to: `md` for forms, `3xl` for forms whose groups lay out in
 * columns, `5xl` for settings and detail lists, `full` for tables and canvases.
 */
const PAGE_WIDTHS = {
  md: "max-w-md",
  "3xl": "max-w-3xl",
  "5xl": "max-w-5xl",
  full: "max-w-full",
} as const;

export type PageWidth = keyof typeof PAGE_WIDTHS;

/**
 * The page padding, moved into the breadcrumb row so its divider spans the
 * full width. The row's buttons set its height, so the top padding scales like
 * the page padding (half, three quarters, full); at lg the crumbs, aligned to
 * the buttons' bottom, sit where they do on other pages.
 */
const FULL_BLEED_BREADCRUMB_ROW = "px-4 pt-2 sm:px-6 sm:pt-3 lg:px-8 lg:pt-4";

export interface PageWrapperProps {
  children: React.ReactNode;
  /** How wide the page content may grow */
  width?: PageWidth;
  /** Whether the page should have height fixed to viewport height (like a chat view) */
  fixedHeight?: boolean;
  /**
   * Fill the viewport below the breadcrumb bar edge to edge, with no page
   * padding or scroll (like a canvas editor). Overrides `width` and
   * `fixedHeight`.
   */
  fullBleed?: boolean;
  /** Content for the breadcrumb row; see `PageBreadcrumbsProps` */
  breadcrumbs?: Pick<PageBreadcrumbsProps, "actions" | "endContent">;
  /** If an error is provided, it will be displayed instead of the main content */
  error?: React.ReactNode;
  className?: string;
  classNames?: SlotsToClasses<"base" | "main">;
  ref?: React.Ref<HTMLDivElement>;
}

/** A layout wrapper component for main content that provides consistent spacing and optional breadcrumb navigation. */
export const PageWrapper = ({
  children,
  width = "md",
  fixedHeight = false,
  fullBleed = false,
  breadcrumbs,
  error,
  className,
  classNames,
  ref,
}: PageWrapperProps) => {
  return (
    <div
      ref={ref}
      className={cn(
        "@container flex flex-col w-full",
        fullBleed
          ? // 3.5rem: the mobile top bar in AppLayout
            "h-[calc(100dvh-3.5rem)] md:h-dvh overflow-hidden"
          : "gap-2 sm:gap-4 p-4 sm:p-6 lg:p-8",
        !fullBleed && fixedHeight
          ? "h-[calc(100vh-(calc(16*var(--spacing))))] md:h-screen"
          : "",
        className,
        classNames?.base,
      )}
    >
      {/* Rendered without a condition: a page whose route carries crumb
          metadata needs no configuration, and PageBreadcrumbs draws nothing
          without it. */}
      <PageBreadcrumbs
        {...breadcrumbs}
        classNames={fullBleed ? { row: FULL_BLEED_BREADCRUMB_ROW } : undefined}
      />
      {error ? (
        <ErrorView message={error} />
      ) : (
        <main
          id="main-content"
          className={cn(
            `flex flex-col gap-2 sm:gap-4 w-full`,
            fullBleed ? "flex-1 min-h-0" : PAGE_WIDTHS[width],
            !fullBleed && fixedHeight
              ? "h-full &>*:flex-shrink &>*:flex-grow-0 &>*:min-h-0"
              : "",
            classNames?.main,
          )}
        >
          {children}
        </main>
      )}
    </div>
  );
};
